export enum ControllerModeEnum {
  MODE_1 = 'mode1',
  MODE_2 = 'mode2',
}

export enum CameraActionEnum {
  NONE = '-',
  TILT_UP = 'Tilt Up',
  TILT_DOWN = 'Tilt Down',
  PAN_LEFT = 'Pan Left',
  PAN_RIGHT = 'Pan Right',
  ZOOM_IN = 'Zoom In',
  ZOOM_OUT = 'Zoom Out',
  START_STOP_VIDEO_RECORDING = 'Start / Stop Video Recording',
}

export const CONTROLLER_MODE_OPTIONS = [
  { value: ControllerModeEnum.MODE_1, name: 'Mode 1' },
  { value: ControllerModeEnum.MODE_2, name: 'Mode 2' },
];

export const CAMERA_ACTION_OPTIONS = [
  { value: CameraActionEnum.NONE, name: '-' },
  { value: CameraActionEnum.TILT_UP, name: 'Tilt Up' },
  { value: CameraActionEnum.TILT_DOWN, name: 'Tilt Down' },
  { value: CameraActionEnum.PAN_LEFT, name: 'Pan Left' },
  { value: CameraActionEnum.PAN_RIGHT, name: 'Pan Right' },
  { value: CameraActionEnum.ZOOM_IN, name: 'Zoom In' },
  { value: CameraActionEnum.ZOOM_OUT, name: 'Zoom Out' },
  { value: CameraActionEnum.START_STOP_VIDEO_RECORDING, name: 'Start / Stop Video Recording' },
];

export const STICK_MAPPINGS: Record<ControllerModeEnum, { lStickUpDown: string; lStickLeftRight: string; rStickUpDown: string; rStickLeftRight: string }> = {
  [ControllerModeEnum.MODE_1]: {
    lStickUpDown: 'Pitch',
    lStickLeftRight: 'Roll',
    rStickUpDown: 'Throttle',
    rStickLeftRight: 'Yaw',
  },
  [ControllerModeEnum.MODE_2]: {
    lStickUpDown: 'Throttle',
    lStickLeftRight: 'Yaw',
    rStickUpDown: 'Pitch',
    rStickLeftRight: 'Roll',
  },
};

export type CameraControlKey =
  | 'dPadUp'
  | 'dPadDown'
  | 'dPadLeft'
  | 'dPadRight'
  | 'l2'
  | 'r2'
  | 'circle'
  | 'cross'
  | 'square'
  | 'triangle'
  | 'l1'
  | 'r1';

export const CAMERA_CONTROL_LABELS: Record<CameraControlKey, string> = {
  dPadUp: 'D-Pad Up',
  dPadDown: 'D-Pad Down',
  dPadLeft: 'D-Pad Left',
  dPadRight: 'D-Pad Right',
  l2: 'L2',
  r2: 'R2',
  circle: 'Circle',
  cross: 'Cross',
  square: 'Square',
  triangle: 'Triangle',
  l1: 'L1',
  r1: 'R1',
};

export const DEFAULT_CAMERA_CONTROLS: Record<CameraControlKey, CameraActionEnum> = {
  dPadUp: CameraActionEnum.TILT_UP,
  dPadDown: CameraActionEnum.TILT_DOWN,
  dPadLeft: CameraActionEnum.PAN_LEFT,
  dPadRight: CameraActionEnum.PAN_RIGHT,
  l2: CameraActionEnum.ZOOM_OUT,
  r2: CameraActionEnum.ZOOM_IN,
  circle: CameraActionEnum.START_STOP_VIDEO_RECORDING,
  cross: CameraActionEnum.NONE,
  square: CameraActionEnum.NONE,
  triangle: CameraActionEnum.NONE,
  l1: CameraActionEnum.NONE,
  r1: CameraActionEnum.NONE,
};

// Standard Gamepad API button index → CameraControlKey
export const CAMERA_CONTROL_BUTTON_INDEX: Record<CameraControlKey, number> = {
  cross: 0,
  circle: 1,
  square: 2,
  triangle: 3,
  l1: 4,
  r1: 5,
  l2: 6,
  r2: 7,
  dPadUp: 12,
  dPadDown: 13,
  dPadLeft: 14,
  dPadRight: 15,
};

// Reverse mapping: Gamepad API button index → CameraControlKey
export const BUTTON_INDEX_TO_CAMERA_CONTROL: Record<number, CameraControlKey> = Object.fromEntries(
  Object.entries(CAMERA_CONTROL_BUTTON_INDEX).map(([key, index]) => [index, key as CameraControlKey])
) as Record<number, CameraControlKey>;

// Axes mapping per controller mode (Gamepad API axes indices)
// axes[0] = L-Stick horizontal, axes[1] = L-Stick vertical
// axes[2] = R-Stick horizontal, axes[3] = R-Stick vertical
export interface AxesMapping {
  yaw: number;
  throttle: number;
  roll: number;
  pitch: number;
}

export const MODE_AXES_MAPPING: Record<ControllerModeEnum, AxesMapping> = {
  [ControllerModeEnum.MODE_1]: {
    pitch: 1,
    roll: 0,
    throttle: 3,
    yaw: 2,
  },
  [ControllerModeEnum.MODE_2]: {
    throttle: 1,
    yaw: 0,
    pitch: 3,
    roll: 2,
  },
};

// Dead zone threshold for analog sticks (filters out noise in neutral position)
export const JOYSTICK_DEAD_ZONE = 0.1;

export const applyDeadZone = (value: number, threshold = JOYSTICK_DEAD_ZONE): number => {
  if (Math.abs(value) < threshold) return 0;
  const sign = Math.sign(value);
  return Number((sign * (Math.abs(value) - threshold) / (1 - threshold)).toFixed(2));
};

// User settings storage keys
export const USER_SETTING_KEYS = {
  CONTROLLER_MODE: 'controller_mode',
  CAMERA_CONTROLS: 'camera_controls',
} as const;
